import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TbFlag, TbFlagFilled } from 'react-icons/tb';
import './style.css';
import CreateCompanyFooter from '../components/createcompanyfooter/CreateCompanyFooter';
import { FaRegStar, FaStar } from 'react-icons/fa6';
import { MdAccessTimeFilled } from 'react-icons/md';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBaseUrlFinder';
import { AiFillDislike, AiFillLike, AiOutlineDislike, AiOutlineLike } from 'react-icons/ai';

const AllReviews = () => {
  const [engagements, setEngagements] = useState({});
  const [engagementsLoaded, setEngagementsLoaded] = useState(false);
  const [reviewData, setReviewData] = useState([]);

  // Load cached engagements on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem('userEngagements');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          setEngagements(parsed);
        }
      }
      setEngagementsLoaded(true);
    } catch (e) {
      console.error('Error loading cached engagements:', e);
    }
  }, []);

  // Persist engagements whenever they change
  useEffect(() => {
    try {
      if (!engagementsLoaded) return;
      // Avoid overwriting existing cache with empty object before load
      localStorage.setItem('userEngagements', JSON.stringify(engagements));
    } catch (e) {
      console.error('Error saving engagements cache:', e);
    }
  }, [engagements, engagementsLoaded]);

  const handleEngagement = async (reviewId, type) => {
    const prev = engagements[reviewId] || { like: false, dislike: false, reported: false };

    // Block duplicate actions and prevent switching between like/dislike; report is one-time
    if ((type === 'like' && (prev.like || prev.dislike)) || 
        (type === 'dislike' && (prev.dislike || prev.like)) || 
        (type === 'report' && prev.reported)) {
      return;
    }

    // Update local engagement cache
    const next = { ...prev };
    if (type === 'like') {
      next.like = true;
      next.dislike = false;
    } else if (type === 'dislike') {
      next.dislike = true;
      next.like = false;
    } else if (type === 'report') {
      next.reported = true; // one-time
    }
    const updatedEngagements = { ...engagements, [reviewId]: next };
    setEngagements(updatedEngagements);

    const review = reviewData.find((r) => r.review_id === reviewId);
    if (!review) {
      console.error('Review not found for ID:', reviewId);
      return;
    }

    // Optimistic count update (no toggle-off, only increments; switching adjusts)
    setReviewData((prevData) =>
      prevData.map((r) => {
        if (r.review_id !== reviewId) return r;

        let updatedLikes = r.likes;
        let updatedDislikes = r.dislikes;

        if (type === 'like') {
          updatedLikes += 1;
          if (prev.dislike) updatedDislikes -= 1;
        } else if (type === 'dislike') {
          updatedDislikes += 1;
          if (prev.like) updatedLikes -= 1;
        }

        return { ...r, likes: updatedLikes, dislikes: updatedDislikes };
      })
    );

    // Build payload; for report send like/dislike false and reported true
    const isReport = type === 'report';
    const payload = {
      manager_id: review.manager_id,
      manager_full_name: review.manager_full_name,
      review_id: reviewId,
      like: isReport ? false : next.like,
      dislike: isReport ? false : next.dislike,
      reported: isReport ? true : false,
    };

    try {
      const { data } = await axios.put(`${getApiBaseUrl()}/review_engagement`, payload);
      const serverProfile = data?.review_profile;
      if (serverProfile) {
        setReviewData((prevData) =>
          prevData.map((r) =>
            r.review_id === reviewId ? { ...r, likes: serverProfile.likes, dislikes: serverProfile.dislikes } : r
          )
        );
      }
    } catch (error) {
      console.error('Error updating engagement:', error);
    }
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/trending_reviews?review_count=20`);
        const data = await response.json();
        setReviewData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  return (
    <>
      <div className="container">
        <div className="row"><div className="col-12" /></div>

        <div className="row">
          <div className="col-lg-8 col-md-9 col-12">
            <div>
              {reviewData?.map((review) => (
                <div key={review?.review_id} className="my-lg-4 my-md-4 my-3">
                  <div>
                    <div className="d-flex justify-content-end pb-1">
                      <div className="me-2">
                        {review?.is_verified ? (
                          <div className="verified-tooltip-trending ms-2">
                            <span className="verified-badge-trending">Verified</span>
                            <span className="verified-tooltip-text">
                              Verified reviews are submitted by individuals who have confirmed their employment.
                            </span>
                          </div>
                        ) : (
                          <div className="unverified-tooltip-trending ms-2">
                            <span className="unverified-badge-trending">Unverified</span>
                            <span className="unverified-tooltip-text">
                              This review is by an unverified user and will not count towards the manager&apos;s official rating.
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="mb-0">
                        <MdAccessTimeFilled color="#9C9C9C" />
                      </p>
                      <p className="ps-1 access_time_now pt-1 mb-0">{review?.creation_time}</p>
                    </div>
                  </div>

                  <div className="all_reviews_box" style={{ backgroundColor: '#ffffff' }}>
                    <div className="py-3 px-lg-4 px-md-3 px-3">
                      <div className="row">
                        <div className="col-lg-12 col-md-12 col-12">
                          <div>
                            <div className="d-flex justify-content-between mb-2">
                              <div>
                                <Link
                                  to={`/company-profile?name=${encodeURIComponent(review?.company_name || '')}&id=${review?.company_id || ''}`}
                                  style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                  }}
                                >
                                  {review?.company_logo_url ? (
                                    <img
                                      src={review.company_logo_url}
                                      alt={`${review?.company_name || 'Company'} logo`}
                                      style={{
                                        width: '28px',
                                        height: '28px',
                                        objectFit: 'contain',
                                        borderRadius: '4px',
                                        backgroundColor: '#fff'
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '4px',
                                        backgroundColor: '#eee'
                                      }}
                                    />
                                  )}
                                  <h4 className="all_reviews_h4 mb-0">{review?.company_name}</h4>
                                </Link>
                              </div>
                              <div>
                                {engagements?.[review.review_id]?.like ? (
                                  <AiFillLike
                                    fontSize={22}
                                    color="#151515"
                                    onClick={() => handleEngagement(review.review_id, 'like')}
                                    style={{ cursor: (engagements?.[review.review_id]?.like || engagements?.[review.review_id]?.dislike) ? 'not-allowed' : 'pointer' }}
                                  />
                                ) : (
                                  <AiOutlineLike
                                    fontSize={22}
                                    color="#151515"
                                    onClick={() => handleEngagement(review.review_id, 'like')}
                                    style={{ cursor: (engagements?.[review.review_id]?.like || engagements?.[review.review_id]?.dislike) ? 'not-allowed' : 'pointer' }}
                                  />
                                )}
                                <span className="add_review_p mx-1 fw-bold">{review?.likes}</span>
                                {engagements?.[review.review_id]?.dislike ? (
                                  <AiFillDislike
                                    fontSize={25}
                                    color="#151515"
                                    className="ms-2"
                                    onClick={() => handleEngagement(review.review_id, 'dislike')}
                                    style={{ cursor: (engagements?.[review.review_id]?.dislike || engagements?.[review.review_id]?.like) ? 'not-allowed' : 'pointer' }}
                                  />
                                ) : (
                                  <AiOutlineDislike
                                    fontSize={25}
                                    color="#151515"
                                    className="ms-2"
                                    onClick={() => handleEngagement(review.review_id, 'dislike')}
                                    style={{ cursor: (engagements?.[review.review_id]?.dislike || engagements?.[review.review_id]?.like) ? 'not-allowed' : 'pointer' }}
                                  />
                                )}
                                <span className="add_review_p ms-1 fw-bold">{review?.dislikes}</span>
                              </div>
                            </div>

                            <div className="d-lg-flex d-md-flex">
                              <div>
                                <Link
                                  to={`/manager?name=${encodeURIComponent(review?.manager_full_name || '')}&id=${review?.manager_id || ''}`}
                                  style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                  <h6 className="all_reviews_h6_details fw-bold">{review?.manager_full_name}</h6>
                                </Link>
                              </div>
                              <div className="border_difference_tags_difference ms-2 me-2"></div>
                              <div>
                                <h6 className="all_reviews_h6_details">{review?.manager_position}</h6>
                              </div>
                              <div className="border_difference_tags_difference ms-2 me-2"></div>
                              <div>
                                <h6 className="all_reviews_h6_details">{review?.manager_location}</h6>
                              </div>
                            </div>
                          </div>

                          <div>
                            <p className="mb-2">
                              {Array.from({ length: 5 }, (_, i) =>
                                i < (Number(review?.review_rating) || 0) ? (
                                  <FaStar key={i} color="#000000" />
                                ) : (
                                  <FaRegStar key={i} />
                                )
                              )}
                            </p>
                          </div>

                          <div className="d-flex align-items-center justify-content-between pt-lg-0 pt-md-0 pt-3">
                            <div>
                              <h5 className="mb-0 tags_manager_profile">Tags</h5>
                            </div>
                          </div>

                          <div className="d-lg-flex d-md-flex align-items-center">
                            <div>
                              <div className="d-flex flex-wrap gap-2 pt-2">
                                {(review?.positive_tags || []).map((tag, idx) => (
                                  <div
                                    key={`pos-${review?.review_id}-${idx}-${tag}`}
                                    className="px-3 py-1"
                                    style={{
                                      backgroundColor: '#EDFDED',
                                      color: '#2C2C2C',
                                      cursor: 'pointer',
                                      borderRadius: '30px',
                                      fontSize: '0.85em',
                                      height: '28px',
                                    }}
                                  >
                                    {tag}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="border_difference_tags ms-2 mt-2"></div>
                            <div>
                              <div className="d-flex flex-wrap gap-2 pt-2 ps-2">
                                {(review?.negative_tags || []).map((tag, idx) => (
                                  <div
                                    key={`neg-${review?.review_id}-${idx}-${tag}`}
                                    className="px-3 py-1"
                                    style={{
                                      backgroundColor: '#FFD0CA',
                                      color: '#2C2C2C',
                                      cursor: 'pointer',
                                      borderRadius: '30px',
                                      fontSize: '0.85em',
                                      height: '28px',
                                    }}
                                  >
                                    {tag}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="manager_reviews_box p-3 mt-lg-4 mt-md-3 mt-3">
                            <p className="manager_reviews_box_p">{review?.review}</p>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="d-flex justify-content-end pt-2">
                          <div>
                            {engagements?.[review?.review_id]?.reported ? (
                              <TbFlagFilled
                                fontSize={25}
                                color="#000000"
                                onClick={() => {}}
                                style={{ cursor: 'not-allowed' }}
                              />
                            ) : (
                              <TbFlag
                                fontSize={25}
                                color="#151515"
                                onClick={() => handleEngagement(review?.review_id, 'report')}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <CreateCompanyFooter />
    </>
  );
};

export default AllReviews;